import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useStore } from '@store/StoreProvider'
import { Link } from 'react-router-dom'
import { toastStore } from '@store/ToastStore'
import { jwtDecode } from 'jwt-decode'
import { postAuthLandingPath } from '@constants/adminNav'
import { RecaptchaNotice } from '@app/auth/RecaptchaNotice'
import { fetchWithRecaptchaRetry, preloadRecaptcha } from '@utils/recaptcha'
import { t } from '@store/I18nStore'


export default function AcceptInvitePage() {
  const { i18n, auth } = useStore();
  /** Optional email from validate-reset-token or accept-invite (not shown in the form). */
  const [resolvedEmail, setResolvedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [inviteToken, setInviteToken] = useState('');
  const hasValidated = React.useRef(false);

  useEffect(() => {
    preloadRecaptcha();
  }, []);

  useEffect(() => {

    // Prevent duplicate calls in React Strict Mode
    if (hasValidated.current) return;
    hasValidated.current = true;

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) {
      toastStore.show(t('errors.auth.token_not_found'), "error");
      setIsValidatingToken(false);
      return;
    }

    setInviteToken(token);
    validateToken(token);
  }, []);

  const validateToken = async (token: string) => {
    setIsValidatingToken(true);
    try {
      const response = await fetch(`${i18n.apiBaseUrl.replace(/\/$/, '')}/api/auth/validate-reset-token?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = (await response.json()) as {
        success?: boolean;
        message?: string;
        error?: string;
        email?: string;
        userEmail?: string;
        Email?: string;
      };

      // API may return e.g. { "success": true, "message": "Токен валиден" } without email
      const okBody = response.ok && data.success !== false;

      if (okBody) {
        setTokenValid(true);
        const raw =
          typeof data.email === 'string'
            ? data.email
            : typeof data.userEmail === 'string'
              ? data.userEmail
              : typeof data.Email === 'string'
                ? data.Email
                : '';
        const trimmed = raw.trim();
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
          setResolvedEmail(trimmed);
        }
      } else {
        setTokenValid(false);
        const errorMessage = data.message || data.error || t('errors.auth.link_token_invalid');
        toastStore.show(errorMessage, "error");
      }
    } catch (error) {
      setTokenValid(false);
      toastStore.show(t('errors.auth.check_token'), "error");
      console.error('Token validation error:', error);
    } finally {
      setIsValidatingToken(false);
    }
  };

  const clearError = (field: string) => {
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validatePasswordStrength = (pwd: string) => {
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);

    return {
      isValid: hasUpperCase && hasLowerCase && hasNumber,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
    };
  };

  const handleSubmit = async () => {
    setErrors({});

    const newErrors: Record<string, string> = {};
    let isValid = true;

    if (!password) {
      newErrors.password = t('errors.auth.password_required');
      isValid = false;
    } else if (password.length < 8) {
      newErrors.password = t('errors.auth.password_min_length');
      isValid = false;
    } else {
      const passwordCheck = validatePasswordStrength(password);
      if (!passwordCheck.isValid) {
        const missing = [];
        if (!passwordCheck.hasUpperCase) missing.push(t('auth.password_rules.uppercase'));
        if (!passwordCheck.hasLowerCase) missing.push(t('auth.password_rules.lowercase'));
        if (!passwordCheck.hasNumber) missing.push(t('auth.password_rules.digit'));
        newErrors.password = t('auth.password_rules.must_contain', { rules: missing.join(', ') });
        isValid = false;
      }
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t('auth.password.repeat');
      isValid = false;
    } else if (password && confirmPassword !== password) {
      newErrors.confirmPassword = t('errors.auth.passwords_mismatch');
      isValid = false;
    }

    if (!isValid) {
      setErrors(newErrors);
      if (Object.keys(newErrors).length > 0) {
        toastStore.show(t('errors.fix_form'), "error");
      }
      return;
    }

    setIsLoading(true);
    try {
      const response: Response = await fetch(`${i18n.apiBaseUrl.replace(/\/$/, '')}/api/auth/accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: inviteToken,
          newPassword: password
        }),
      });

      const data = (await response.json()) as {
        message?: string;
        error?: string;
        token?: string;
        email?: string;
        userEmail?: string;
        errors?: Record<string, string>;
      };

      if (response.ok) {
        toastStore.show(data.message || t('auth.invite.accepted'), "success");

        const emailFromInvite =
          (typeof data.email === 'string' && data.email.trim()) ||
          (typeof data.userEmail === 'string' && data.userEmail.trim()) ||
          resolvedEmail.trim();

        const finishWithSessionToken = (sessionToken: string) => {
          auth.setToken(sessionToken);
          const user = jwtDecode(sessionToken) as {
            id: number;
            email?: string;
            role: string;
            iat: string;
          };
          const displayEmail = user.email ?? emailFromInvite;
          auth.setUser({
            id: user.id,
            email: displayEmail,
            role: user.role,
            createdAt: user.iat,
          });
          toastStore.show(t('auth.success.sign_in'), "success");
          setTimeout(() => {
            window.location.href = postAuthLandingPath(user.role);
          }, 1000);
        };

        try {
          if (typeof data.token === 'string' && data.token.length > 0) {
            finishWithSessionToken(data.token);
          } else if (emailFromInvite) {
            const loginResponse = await fetchWithRecaptchaRetry('login', (captchaToken) =>
              fetch(`${i18n.apiBaseUrl.replace(/\/$/, '')}/api/auth/login`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  email: emailFromInvite,
                  password: password,
                  captchaToken,
                }),
              }),
            );

            const loginData = (await loginResponse.json()) as { token?: string };

            if (loginResponse.ok && loginData.token) {
              finishWithSessionToken(loginData.token);
            } else {
              toastStore.show(t('auth.success.registration_complete'), "success");
              setTimeout(() => {
                window.location.href = '/login';
              }, 1500);
            }
          } else {
            toastStore.show(t('auth.success.registration_complete'), "success");
            setTimeout(() => {
              window.location.href = '/login';
            }, 1500);
          }
        } catch (loginError) {
          console.error('Auto-login error:', loginError);
          toastStore.show(t('auth.success.registration_complete'), "success");
          setTimeout(() => {
            window.location.href = '/login';
          }, 1500);
        }

        setPassword('');
        setConfirmPassword('');
      } else {
        const errorMessage = data.message || data.error || t('errors.auth.accept_invite');
        toastStore.show(errorMessage, "error");

        if (data.errors) {
          const apiErrors: Record<string, string> = {};
          if (data.errors.password) apiErrors.password = data.errors.password;
          if (data.errors.newPassword) apiErrors.password = data.errors.newPassword;
          setErrors(apiErrors);
        }
      }
    } catch (error) {
      toastStore.show(t('errors.server_connection'), "error");
      console.error('Accept invite error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidatingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{
        background: 'linear-gradient(180deg, #0b0f2d, #1b1f4a)',
      }}>
        <div className="text-white text-xl">{t('auth.loading.check_token')}</div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex flex-col" style={{
        background: 'linear-gradient(180deg, #0b0f2d, #1b1f4a)',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, Arial'
      }}>
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute w-[480px] h-[480px] rounded-full blur-[140px] opacity-35"
               style={{ background: 'rgba(100,60,255,.9)', top: '-120px', left: '-160px' }} />
          <div className="absolute w-[560px] h-[560px] rounded-full blur-[140px] opacity-35"
               style={{ background: 'rgba(60,100,255,.8)', bottom: '-180px', right: '-220px' }} />
        </div>

        <main className="flex-1 flex flex-col justify-center items-center px-5 py-10 relative z-10">
          <div className="text-center mb-6">
            <Link to="/" className="text-5xl mb-3">
              <img src="/assets/logo.svg" alt="logo" style={{width: '220px', margin: '0 auto 16px'}}/>
            </Link>
            <h1 className="text-3xl font-extrabold text-white mb-4">{t('errors.auth.invalid_link')}</h1>
            <p className="text-gray-300 mb-6">{t('errors.auth.invite_link_invalid')}</p>
            <a href="/login" className="text-blue-500 font-semibold hover:text-purple-500 transition-colors">{t('auth.go_to_login')}</a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{
      background: 'linear-gradient(180deg, #0b0f2d, #1b1f4a)',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, Arial'
    }}>
      {/* Background blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-[480px] h-[480px] rounded-full blur-[140px] opacity-35"
             style={{ background: 'rgba(100,60,255,.9)', top: '-120px', left: '-160px' }} />
        <div className="absolute w-[560px] h-[560px] rounded-full blur-[140px] opacity-35"
             style={{ background: 'rgba(60,100,255,.8)', bottom: '-180px', right: '-220px' }} />
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col justify-center items-center px-5 py-10 relative z-10">
        <div className="text-center mb-6">
          <Link to="/" className="text-5xl mb-3">
            <img src="/assets/logo.svg" alt="logo" style={{width: '220px', margin: '0 auto 16px'}}/>
          </Link>
          <h1 className="text-3xl font-extrabold text-white">{t('auth.complete_registration_title')}</h1>
        </div>

        <div className="w-full max-w-[460px] p-8 rounded-3xl border"
             style={{
               background: 'rgba(18,10,58,.65)',
               borderColor: 'rgba(255,255,255,.08)',
               boxShadow: '0 16px 60px rgba(0,0,0,.55)'
             }}>
          <div>
            {/* Password field */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearError('password'); }}
                  placeholder={t('auth.password.new_label')}
                  autoComplete="new-password"
                  minLength={8}
                  className={`w-full h-12 px-4 pr-14 rounded-xl text-white text-[15px] outline-none transition-all ${
                    errors.password ? 'border-2 border-red-500 shadow-[0_0_0_2px_rgba(255,85,102,.28)_inset]' : 'border border-white/20'
                  }`}
                  style={{ background: '#0e1232' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors cursor-pointer"
                  aria-label={t('auth.password.show')}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className={`min-h-[18px] text-[13px] text-red-300 mt-1.5 flex items-center gap-1.5 ${errors.password ? 'visible' : 'invisible'}`}>
                <span>⚠️</span>
                <span>{errors.password}</span>
              </div>
            </div>

            {/* Confirm Password field */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); clearError('confirmPassword'); }}
                  placeholder={t('auth.password.repeat')}
                  autoComplete="new-password"
                  className={`w-full h-12 px-4 pr-14 rounded-xl text-white text-[15px] outline-none transition-all ${
                    errors.confirmPassword ? 'border-2 border-red-500 shadow-[0_0_0_2px_rgba(255,85,102,.28)_inset]' : 'border border-white/20'
                  }`}
                  style={{ background: '#0e1232' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors cursor-pointer"
                  aria-label={t('auth.password.show')}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className={`min-h-[18px] text-[13px] text-red-300 mt-1.5 flex items-center gap-1.5 ${errors.confirmPassword ? 'visible' : 'invisible'}`}>
                <span>⚠️</span>
                <span>{errors.confirmPassword}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full mt-2 py-3.5 rounded-xl border-0 font-bold text-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed transition-opacity"
              style={{ background: 'linear-gradient(90deg, #4f6ff5, #8a4fff)' }}
            >
              {isLoading ? t('app.status.saving') : t('auth.complete_registration')}
            </button>
            <RecaptchaNotice />

            <div className="flex justify-center items-center gap-1.5 mt-4 text-sm text-gray-300">
              <span>{t('auth.have_account')}</span>
              <a href="/login" className="text-blue-500 font-semibold hover:text-purple-500 transition-colors">{t('app.actions.sign_in')}</a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
